import {
  Bold,
  Code2,
  Command as CommandIcon,
  Copy,
  Download,
  Eye,
  FilePlus2,
  FolderOpen,
  Heading1,
  Image,
  Italic,
  Link,
  List,
  ListChecks,
  Maximize2,
  Moon,
  PanelLeft,
  PanelRight,
  Redo2,
  Save,
  Search,
  Settings,
  Strikethrough,
  Sun,
  Table2,
  Undo2,
} from "lucide-react";
import { ThemeMode } from "../types";

export interface ToolbarAction {
  id: string;
  label: string;
  icon: JSX.Element;
}

interface ToolbarProps {
  theme: ThemeMode;
  onFormat: (action: string) => void;
  onOpenCommandPalette: () => void;
  onNew: () => void;
  onOpen: () => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onFind: () => void;
  onCopyHtml: () => void;
  onToggleFullscreen: () => void;
  onOpenSettings: () => void;
  onToggleTheme: () => void;
  onTogglePreview: () => void;
  previewVisible: boolean;
  isFullscreen: boolean;
}

const formatActions: ToolbarAction[] = [
  { id: "heading", label: "Heading", icon: <Heading1 size={17} /> },
  { id: "bold", label: "Bold", icon: <Bold size={17} /> },
  { id: "italic", label: "Italic", icon: <Italic size={17} /> },
  { id: "strike", label: "Strikethrough", icon: <Strikethrough size={17} /> },
  { id: "link", label: "Link", icon: <Link size={17} /> },
  { id: "image", label: "Image", icon: <Image size={17} /> },
  { id: "unordered-list", label: "Bullet list", icon: <List size={17} /> },
  { id: "task-list", label: "Task list", icon: <ListChecks size={17} /> },
  { id: "table", label: "Table", icon: <Table2 size={17} /> },
  { id: "code", label: "Code block", icon: <Code2 size={17} /> },
];

export const Toolbar = ({
  theme,
  onFormat,
  onOpenCommandPalette,
  onNew,
  onOpen,
  onSave,
  onUndo,
  onRedo,
  onFind,
  onCopyHtml,
  onToggleFullscreen,
  onOpenSettings,
  onToggleTheme,
  onTogglePreview,
  previewVisible,
  isFullscreen,
}: ToolbarProps) => (
  <header className="toolbar" aria-label="Application toolbar">
    <div className="toolbar-group">
      <button className="icon-button" type="button" onClick={onNew} title="New document" aria-label="New document">
        <FilePlus2 size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onOpen} title="Open Markdown file" aria-label="Open Markdown file">
        <FolderOpen size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onSave} title="Save Markdown" aria-label="Save Markdown">
        <Save size={17} />
      </button>
    </div>
    <div className="toolbar-group formatting-group" aria-label="Formatting tools">
      {formatActions.map((action) => (
        <button
          className="icon-button"
          type="button"
          key={action.id}
          onClick={() => onFormat(action.id)}
          title={action.label}
          aria-label={action.label}
        >
          {action.icon}
        </button>
      ))}
    </div>
    <div className="toolbar-group">
      <button className="icon-button" type="button" onClick={onUndo} title="Undo" aria-label="Undo">
        <Undo2 size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onRedo} title="Redo" aria-label="Redo">
        <Redo2 size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onFind} title="Find & replace" aria-label="Find and replace">
        <Search size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onCopyHtml} title="Copy rendered HTML" aria-label="Copy rendered HTML">
        <Copy size={17} />
      </button>
    </div>
    <div className="toolbar-group toolbar-tail">
      <button className="icon-button" type="button" onClick={onOpenCommandPalette} title="Command palette" aria-label="Command palette">
        <CommandIcon size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onTogglePreview} title="Toggle preview" aria-label="Toggle preview">
        {previewVisible ? <PanelRight size={17} /> : <Eye size={17} />}
      </button>
      <button
        className="icon-button"
        type="button"
        onClick={onToggleFullscreen}
        title={isFullscreen ? "Exit full screen" : "Full screen editing"}
        aria-label={isFullscreen ? "Exit full screen" : "Full screen editing"}
      >
        <Maximize2 size={17} />
      </button>
      <button className="icon-button" type="button" onClick={onToggleTheme} title="Toggle theme" aria-label="Toggle theme">
        {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
      </button>
      <button className="icon-button" type="button" onClick={onOpenSettings} title="Settings" aria-label="Settings">
        <Settings size={17} />
      </button>
      <a className="export-anchor" href="#export-menu">
        <Download size={16} />
        Export
      </a>
    </div>
  </header>
);
